/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component, Fragment } from "react";
import { simpleFetch } from "../../restWrapper";
import { API_ENDPOINT } from "../../config";
import PropTypes from "prop-types";
import { NumericFormat } from "react-number-format";
import PhoneInput, { formatPhoneNumberIntl } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import FileViewer from "./FileViewer";

import { isObjectEmpty, mapManualPaymentName, isValidManualPaymentName, capitalizeWords } from "../../assets/helper";
import { renderErrorMsg } from "../../assets/component/renderErrorMsg.js";
import { getPaymentProjectLabel } from "../../assets/projectTitleHelper.js";
import hkRegions, { getDistrictsByRegion, getLabel, countryList } from "../../assets/hkAddressData.js";

import StripeReconciliation, { StripeSettlementRows, stripeGross, stripeText, isStripeVerified } from "./StripeReconciliation";
import PaymeReconciliation, { PaymeSettlementRows, paymeGross, paymeText, isPaymeVerified } from "./PaymeReconciliation";
import DefaultFormData from "./DefaultFormData";

class RenderPaymentDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      issuingReceipt: false,
      paymentDetail: {},
      isEditMode: false,
      formData: [],
      errorMsg: {},
      loadingProofOfPayment: false,
      showApprovalDropdown: false,
      showDeclineInput: false,
      showPDFViewer: false,
      pdfUrl: null,
      showFeeBreakdown: false,
      showActionsMenu: false,
      stripeChecking: false,
    };

    this.statusDropdownRef = React.createRef();
    this.declineTextareaRef = React.createRef();
    this.actionsMenuRef = React.createRef();
    this.stripeSyncRef = React.createRef();
    this.paymeSyncRef = React.createRef();

    this.mapFormData = this.mapFormData.bind(this);
    this.mapPaymentStatusValue = this.mapPaymentStatusValue.bind(this);
    this.mapPaymentStatusText = this.mapPaymentStatusText.bind(this);

    this.handleToggleEditMode = this.handleToggleEditMode.bind(this);
    this.handleSaveBtn = this.handleSaveBtn.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleViewProofOfPayment = this.handleViewProofOfPayment.bind(this);
    this.handleChangeStatus = this.handleChangeStatus.bind(this);
    this.handleToggleApprovalDropdown = this.handleToggleApprovalDropdown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleClosePDFViewer = this.handleClosePDFViewer.bind(this);
    this.handleDeclineClick = this.handleDeclineClick.bind(this);
    this.handleSendDecline = this.handleSendDecline.bind(this);
    this.handleToggleFeeBreakdown = this.handleToggleFeeBreakdown.bind(this);
    this.handleToggleActionsMenu = this.handleToggleActionsMenu.bind(this);
    this.handleStripeCheckingChanged = this.handleStripeCheckingChanged.bind(this);
  }

  handleToggleFeeBreakdown() {
    this.setState(prevState => ({
      showFeeBreakdown: !prevState.showFeeBreakdown,
    }));
  }

  handleToggleActionsMenu() {
    this.setState(prevState => ({
      showActionsMenu: !prevState.showActionsMenu,
    }));
  }

  handleStripeCheckingChanged(checking, paymentId, result) {
    if (this.props.id !== paymentId || this.state.paymentDetail.id !== paymentId) return;
    const previous = this.state.stripeCheckPaymentId === paymentId ? this.state.stripeCheckResult : null;
    const next = result === undefined ? previous : result;
    if (this.state.stripeChecking !== checking || previous !== next || this.state.stripeCheckPaymentId !== paymentId) {
      if (!checking && result && (this.state.stripeChecking || previous !== next) && this.props.onVerificationChange) {
        this.props.onVerificationChange(paymentId);
      }
      this.setState({ stripeChecking: checking, stripeCheckPaymentId: paymentId, stripeCheckResult: next });
    }
  }

  handlePaymeCheckingChanged = (checking, paymentId, result) => {
    if (this.props.id !== paymentId || this.state.paymentDetail.id !== paymentId) return;
    const previous = this.state.paymeCheckPaymentId === paymentId ? this.state.paymeCheckResult : null;
    const next = result === undefined ? previous : result;
    if (this.state.paymeChecking !== checking || previous !== next || this.state.paymeCheckPaymentId !== paymentId) {
      if (!checking && result && this.props.onVerificationChange) this.props.onVerificationChange(paymentId);
      this.setState({ paymeChecking: checking, paymeCheckPaymentId: paymentId, paymeCheckResult: next });
    }
  };

  handleProviderSync(provider) {
    this.setState({ showActionsMenu: false });
    const ref = provider === 'Stripe' ? this.stripeSyncRef : this.paymeSyncRef;
    if (ref.current) ref.current.sync();
  }

  canManageManualPayments() {
    const { organizationObj } = this.props;
    const plan = organizationObj && organizationObj.currentPlan
      ? String(organizationObj.currentPlan).toLowerCase()
      : '';
    return plan === 'premium';
  }

  componentDidMount() {
    this.fetchData(this.props.id);
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.id !== this.props.id ||
      prevProps.refreshKey !== this.props.refreshKey
    ) {
      if (prevProps.id !== this.props.id) this.setState({ stripeChecking: false, paymeChecking: false,
        stripeCheckPaymentId: null, paymeCheckPaymentId: null, stripeCheckResult: null, paymeCheckResult: null });
      this.fetchData(this.props.id);
    }

    if (
      prevState.paymentDetail !== this.state.paymentDetail &&
      !isObjectEmpty(this.state.paymentDetail)
    ) {
      this.setState({
        formData: this.mapFormData(),
      });
    }
  }

  fetchData(id) {
    if (!id || this.state.loading) return false;
    this.setState({
      loading: true,
    });
    const fetchLink = "/organizations/payments/" + id;
    simpleFetch(fetchLink, "GET").then((res) => {
      this.setState({
        loading: false,
      });
      if (res.status !== 200) {
        this.setState({
          paymentDetail: {},
          isEditMode: false,
          errorMsg: {},
        });
        return null;
      }
      this.setState({
        paymentDetail: res.body,
        receiptUrl: res.body.receiptUrl,
        isEditMode: false,
        errorMsg: {},
      });
    });
  }

  async refreshStripePayment(id) {
    try {
      const response = await simpleFetch("/organizations/payments/" + id, "GET");
      if (this.props.id !== id || response.status !== 200) return false;
      this.setState({ paymentDetail: response.body, receiptUrl: response.body.receiptUrl,
        isEditMode: false, errorMsg: {} });
      return true;
    } catch (_) { return false; }
  }

  mapFormData() {
    const { organizationObj, missions, textObj } = this.props;
    const { paymentDetail } = this.state;
    const { additionalData } = paymentDetail;
    const paymentTypeOptions = (textObj && textObj.paymentTypeOptions) || {};

    let newFormData = DefaultFormData();

    newFormData = newFormData.map((field) => {
      let value = paymentDetail[field.paymentKey] || '';
      let additionalData = {};

      if (field.id === 'projectId') {
        let projectName = '';

        if (value) {
          // Shared with the payment list: the title stored on the payment
          // first, then the loaded project list. Anything missing from the
          // paginated project list used to leave Item blank, which read as
          // "no project" (CP-274).
          projectName = getPaymentProjectLabel(
            this.props.language,
            paymentDetail,
            missions
          );
        } else {
          if (
            paymentDetail.projectName === 'General Payment' ||
            paymentDetail.projectName === 'General Donation'
          ) {
            // set as quick payment data
            value = 0;
            projectName = paymentDetail.projectName;
          }
        }

        const missionOptions = missions.map(mission => {
          let label = mission.title;
          if (mission.status === 'closed') {
            label += ` (${capitalizeWords(mission.status)})`;
          }

          return {
            label: label,
            value: mission.id,
            disabled: mission.status === 'closed',
          }
        })

        const staticOptions = {
          label: `General ${capitalizeWords(paymentDetail.type)}`,
          value: 0,
          disabled: false,
        }

        additionalData = {
          valueText: projectName,
          options: field.options.concat(missionOptions, staticOptions),
        }
      }

      if (field.id === 'paymentType') {
        additionalData = {
          valueText: paymentTypeOptions[value] || capitalizeWords(value),
          disabled: paymentDetail.projectId ? true : false,
          options: [
            { label: paymentTypeOptions.payment || "Payment", value: "payment" },
            { label: paymentTypeOptions.donation || "Donation", value: "donation" },
          ],
        };
      }

      if (field.id === 'paymentName') {
        additionalData = {
          valueText: mapManualPaymentName(value),
        }
      }

      if (field.id === 'status') {
        additionalData = {
          valueText: this.mapPaymentStatusText(value),
        };
      }

      if (field.id === 'transactionFee') {
        const rawVal = paymentDetail[field.paymentKey];
        value = (rawVal != null && rawVal !== '') ? Number(rawVal) : 0;
      }

      if (field.id === 'net') {
        if (paymentDetail.net != null && paymentDetail.net !== '') {
          value = Number(paymentDetail.net);
        } else {
          const amt = Number(paymentDetail.amount) || 0;
          const fee = Number(paymentDetail.fee) || 0;
          value = Math.max(0, amt - fee);
        }
      }

      if (field.id === 'amount') {
        // "Amount" surfaces what the donor was actually charged. Donor pays
        // net + fees by definition. For legacy cover-fee payments that stored
        // the donor's target instead of the gross charged amount, this
        // recovers the correct charged amount.
        const rawAmt = Number(paymentDetail.amount) || 0;
        const fee = Number(paymentDetail.fee) || 0;
        const net = paymentDetail.net != null && paymentDetail.net !== ''
          ? Number(paymentDetail.net)
          : Math.max(0, rawAmt - fee);
        value = paymentDetail.stripeFeeStatus ? stripeGross(paymentDetail) :
          paymentDetail.PaymentMode === 'PayMe' ? paymeGross(paymentDetail) : Math.max(rawAmt, net + fee);
      }

      return {
        ...field,
        value,
        ...additionalData,
      };
    });

    // map custom fields
    if (additionalData && additionalData.length > 0) {
      const mappedAdditionalData = additionalData.map((field) => {
        const formRows = (organizationObj.formSetting && organizationObj.formSetting.rows) || [];
        const getFormSetting = formRows.find((setting) => setting.id === field.id);

        let deleted = false;
        let archived = false;

        if (getFormSetting) {
          archived = getFormSetting.archived;
        } else {
          deleted = true;
        }

        // Use field's own i18n if available, otherwise fall back to formSetting definition
        const i18n = field.i18n || (getFormSetting && getFormSetting.i18n) || { label: {}, placeholder: {} };
        const label = (i18n.label && (i18n.label[this.props.language] || i18n.label.en)) || field.id;
        const placeholder = (i18n.placeholder && (i18n.placeholder[this.props.language] || i18n.placeholder.en)) || '';

        // Use field's own type if available, otherwise fall back to formSetting definition
        let type = field.type || (getFormSetting && getFormSetting.type) || '';
        // Fallback: detect type from value when type is unknown
        if (!type && field.value) {
          if (field.value === 'yes' || field.value === 'no') {
            type = 'yesno';
          } else if (field.value.charAt(0) === '{') {
            try {
              const parsed = JSON.parse(field.value);
              if (parsed.region || parsed.address1 || parsed.country) {
                type = 'address';
              }
            } catch (e) { /* not JSON */ }
          }
        }
        if (!type) type = 'text';

        return {
          id: field.id,
          label,
          placeholder,
          value: field.value,
          type,
          required: field.required,
          isCustomField: true,
          deleted,
          archived,
        };
      });

      newFormData = newFormData.concat(mappedAdditionalData);
    }

    return newFormData;
  }

  mapPaymentStatusValue(status) {
    let newStatus = status;

    if (status === "complete") {
      newStatus = 'approved';
    }

    if (status === "error") {
      newStatus = 'declined';
    }

    if (status === "pending_for_confirmation") {
      newStatus = 'pending';
    }

    return newStatus
  }

  mapPaymentStatusText(status) {
    const { textObj } = this.props;

    let newStatus = status;

    if (status === "complete") {
      newStatus = textObj.status["complete"];
    }

    if (status === "error") {
      newStatus = textObj.status["error"];
    }

    if (status === "processing") {
      newStatus = textObj.status["processing"];
    }

    if (status === "scheduled") {
      newStatus = textObj.status["scheduled"];
    }

    if (status === "pending_for_confirmation") {
      newStatus = textObj.pendingStatus || 'Pending';
    }

    if (status === "reauthenticate") {
      newStatus = textObj.status["reauthenticate"] || 'Re-authenticate';
    }

    if (status === 'expired') newStatus = textObj.status.expired;
    return newStatus
  }

  handleToggleEditMode() {
    if (!this.canManageManualPayments()) {
      return;
    }
    const { isEditMode } = this.state;

    const newState = !isEditMode;

    if (newState === true && this.props.onEnsureAllProjects) {
      // The project dropdown below is built from the projects held in memory,
      // and that list is only the first page. An org with more projects than a
      // page holds could not reassign a payment to any of the rest — the option
      // simply was not in the list (CP-274). Pull the remainder in now: it is
      // one deliberate click rather than something every page load pays for.
      this.props.onEnsureAllProjects();
    }

    if (newState === false) {
      // reset form data when cancel edit
      const newFormData = this.mapFormData();
      this.setState({
        formData: newFormData,
      });
    }

    this.setState({
      isEditMode: newState,
      showApprovalDropdown: false,
      showDeclineInput: false,
    });
  }

  handleSaveBtn() {
    const { organizationObj } = this.props;
    const { paymentDetail, formData, errorMsg } = this.state;

    let err = errorMsg;

    const minAmount = organizationObj.minimumPaymentAmount || 0;

    formData.forEach((field) => {
      if (field.required && field.value === '') {
        err[field.id] = 'This field is required';
      } else if (field.id === 'amount' && field.value < minAmount) {
        err[field.id] = `Minimum amount HK$${minAmount}`;
      }
    });

    this.setState({
      errorMsg: err,
    });

    if (isObjectEmpty(err)) {
      const mappedFormData = {
        fields: [],
      };

      let isProjectChanged = false;
      let isQuickPayment = false;
      const isManual = paymentDetail.PaymentMode === 'Manual';

      // Fields that only manual payments are allowed to edit
      const manualOnlyFields = ['amount', 'transactionFee', 'status', 'net'];

      formData.forEach((field) => {
        if (!field.isCustomField) {
          // Skip fields that non-manual payments cannot edit
          if (!isManual && manualOnlyFields.includes(field.id)) {
            return;
          }

          let value = field.value;

          if (field.id === 'status') {
            value = this.mapPaymentStatusValue(field.value);
          }

          if (field.id === 'projectId') {
            const prevProjectId = paymentDetail.projectId;

            if (value !== prevProjectId) {
              isProjectChanged = true;
            }
          }

          mappedFormData[field.id] = value;
        } else {
          mappedFormData.fields.push({
            id: field.id,
            value: field.value,
          });
        }
      });

      const getProjectId = formData.find((data) => data.id === 'projectId');

      // override payment data for quick payment
      if (getProjectId && getProjectId.value === 0) {
        mappedFormData.paymentType = 'quick_payment';

        const getType = formData.find((data) => data.id === 'paymentType');
          
        if (getType) {
          mappedFormData.transactionType = getType.value;
          mappedFormData.projectName = `General ${capitalizeWords(getType.value)}`;
        }

        isQuickPayment = true;
      }
      

      this.props.onSaveBtn(paymentDetail.id, mappedFormData, isProjectChanged, isQuickPayment);

      this.setState({
        isEditMode: false,
      }, () => {
        // Re-fetch payment data to get updated receiptStale flag
        this.fetchData(paymentDetail.id);
      });
    }
  }

  handleInputChange(event, field) {
    const { missions } = this.props;
    const { errorMsg, paymentDetail } = this.state;
    const { value } = event.target;


    this.setState((prevState) => {
      const updatedFormData = prevState.formData.map((data) => {
        if (data.id === field.id) {
          let newValue = value;
          let newValueText = null;

          if (field.type === "phone") {
            newValue = formatPhoneNumberIntl(value);
          }

          if (field.type === "decimal") {
            newValue = Number(value);
          }

          if (field.id === 'projectId') {
            newValue = value !== "" ? parseInt(value, 10) : value;
            newValueText = field.options.find((option) => option.value === newValue).label;
          }

          if (field.id === 'paymentName') {
            newValueText = mapManualPaymentName(value);
          }

          if (field.id === 'status') {
            newValueText = this.mapPaymentStatusText(value);
          }

          return {
            ...data,
            value: newValue,
            ...(newValueText && { valueText: newValueText }),
          };
        }

        return data;
      });

      // Calculate net if amount or transaction fee changes
      if (field.id === "amount" || field.id === "transactionFee") {
        const amount = field.id === "amount" 
          ? Number(value) 
          : Number(updatedFormData.find(data => data.id === "amount").value);
        
        const transactionFee = field.id === "transactionFee" 
          ? Number(value) 
          : Number(updatedFormData.find(data => data.id === "transactionFee").value);
        
        // Calculate net amount (payment amount - transaction fee)
        const netAmount = Math.max(0, amount - transactionFee);
        
        // Update the payment net amount field
        updatedFormData.forEach(data => {
          if (data.id === "net") {
            data.value = netAmount;
          }
        });
      }

      // conditional logic for payment type based on selected project
      if (field.id === 'projectId') {
        const newValue = value !== "" ? parseInt(value, 10) : value;

        const ptOptions = (this.props.textObj && this.props.textObj.paymentTypeOptions) || {};
        let paymentTypeValue = 'payment';
        let paymentTypeTextValue = ptOptions.payment || 'Payment';
        let paymentTypeDisabled = false;

        if (newValue !== "") {
          // if the selected project is a quick payment
          paymentTypeValue = paymentDetail.type;
          paymentTypeTextValue = ptOptions[paymentDetail.type] || capitalizeWords(paymentDetail.type);

          if (newValue !== 0) {
            // if the selected project is not a quick payment
            const getProject = missions.find((mission) => mission.id === newValue);

            if (getProject) {
              paymentTypeValue = getProject.donation ? 'donation' : 'payment';
              paymentTypeTextValue = ptOptions[paymentTypeValue] || capitalizeWords(paymentTypeValue);
            }
            
            paymentTypeDisabled = true;
          }
        }

        // Update the payment type field
        updatedFormData.forEach(data => {
          if (data.id === "paymentType") {
            data.value = paymentTypeValue;
            data.valueText = paymentTypeTextValue;
            data.disabled = paymentTypeDisabled;
          }
        });
      }

      let err = errorMsg;
      delete err[field.id];

      return {
        formData: updatedFormData,
        errorMsg: err,
      };
    });
  }

  handleViewProofOfPayment() {
    const { paymentDetail } = this.state;

    if (!paymentDetail.proofOfPayment) {
      window.alert('Error: No proof of payment available');
      return;
    }

    this.setState({
      loadingProofOfPayment: true
    });

    // Use MAIN_DOMAIN from config for backend requests
    const viewerUrl = `${API_ENDPOINT}/organizations/payments/proofofpayment/${paymentDetail.id}`;

    // First try to fetch a small part of the file to verify it's accessible
    fetch(viewerUrl, {
      method: 'HEAD',
      credentials: 'include',
      headers: {
        'Accept': '*/*',
        'Cache-Control': 'no-cache'
      }
    })
      .then(response => {
      
        if (!response.ok) {
          throw new Error(`Failed to access proof of payment: ${response.status}`);
        }
      
        // Show the viewer
        this.setState({
          showPDFViewer: true,
          pdfUrl: viewerUrl,
          loadingProofOfPayment: false
        });
      })
      .catch(err => {
        // console.error('Error checking proof of payment:', err);
        window.alert(`Error accessing the file: ${err.message}. Please try again later.`);
        this.setState({
          loadingProofOfPayment: false
        });
      });
  }

  handleClosePDFViewer() {
    this.setState({
      showPDFViewer: false,
      pdfUrl: null
    });
  }

  handleChangeStatus(status, sendReceipt = false, text = '') {
    if (!this.canManageManualPayments()) {
      return;
    }
    const { paymentDetail } = this.state;
    
    this.setState({
      showApprovalDropdown: false,
      showDeclineInput: false,
    });

    this.props.onChangeStatusBtn(paymentDetail.id, status, sendReceipt, text);
  }

  handleToggleApprovalDropdown() {
    if (!this.canManageManualPayments()) {
      return;
    }
    this.setState(prevState => ({
      showApprovalDropdown: !prevState.showApprovalDropdown,
      showDeclineInput: false,
    }));
  }

  handleClickOutside(event) {
    const { current } = this.statusDropdownRef;

    // Ignore clicks on buttons with specific classes
    // this to prevent interference with other buttons actions
    if (
      event.target.closest('.AccountOverview-backBtn') || 
      event.target.closest('.AccountOverview-receiptContainer') ||
      event.target.closest('.AccountOverview-reissueBtn') ||
      event.target.closest('.AccountOverview-editBtn') ||
      event.target.closest('.AccountOverview-deleteBtn')
    ) {
      return;
    }

    if (
      current &&
      !current.contains(event.target) &&
      (this.state.showApprovalDropdown || this.state.showDeclineInput)
    ) {
      this.setState({
        showApprovalDropdown: false,
        showDeclineInput: false,
      });
    }

    const actionsMenu = this.actionsMenuRef.current;
    if (
      actionsMenu &&
      !actionsMenu.contains(event.target) &&
      this.state.showActionsMenu
    ) {
      this.setState({ showActionsMenu: false });
    }
  }

  handleDeclineClick() {
    if (!this.canManageManualPayments()) {
      return;
    }
    this.setState(prevState => ({
      showDeclineInput: !prevState.showDeclineInput,
    }));
  }

  handleSendDecline() {
    if (!this.canManageManualPayments()) {
      return;
    }
    const declineReason = this.declineTextareaRef.current ? this.declineTextareaRef.current.value : '';
    this.handleChangeStatus('declined', false, declineReason);
  }

  render() {
    if (this.state.loading) {
      return (
        <div className="AccountOverview-emptyPage1">
          <div className="AccountOverview-center">
            <div className="AccountOverview-btm-load"></div>
          </div>
        </div>
      );
    }

    const textObj = this.props.textObj;

    const data = this.state.paymentDetail;

    // console.log(JSON.stringify(data) + " -62");


    if (!data || !textObj) return null;

    /* this.props.id */

    let stripeError;

    if (this.state.paymentDetail.stripeError !== ""){
      stripeError = this.state.paymentDetail.stripeError;
    }

    const paymentId = this.state.paymentDetail.organizationReceiptId || null;

    let lastFour = this.state.paymentDetail.cardLast4;

    if (this.state.paymentDetail.cardLast4 === "") {
      lastFour = "";
    }

    let cardBrand = this.state.paymentDetail.cardBrand;

    if (this.state.paymentDetail.cardBrand === "") {
      cardBrand = "";
    }

    if (this.state.paymentDetail.cardBrand === "visa") {
      cardBrand = "Visa";
    }

    if (this.state.paymentDetail.cardBrand === "mastercard") {
      cardBrand = "Mastercard";
    }

    if (this.state.paymentDetail.cardBrand === "amex") {
      cardBrand = "American Express";
    }

    if (this.state.paymentDetail.cardBrand === "unionpay") {
      cardBrand = "UnionPay";
    }

    let wallet = this.state.paymentDetail.wallet;

    if (this.state.paymentDetail.wallet === "") {
      wallet = "";
    }

    if (this.state.paymentDetail.wallet === "apple_pay") {
      wallet = "Apple Pay |";
    }

    if (
      this.state.paymentDetail.wallet === "google_pay" ||
      this.state.paymentDetail.wallet === "android_pay"
    ) {
      wallet = "Google Pay |";
    }

    let stripePaymentIntentId = this.state.paymentDetail.stripePaymentIntentId;

    if (this.state.paymentDetail.stripePaymentIntentId === "") {
      stripePaymentIntentId = "";
    }

    const paymentMode = this.state.paymentDetail.PaymentMode || '';
    const paymentModeText = `${paymentMode} Payment`;

    const paymentName = this.state.paymentDetail.PaymentName || '';

    const isManualPayment = paymentMode === 'Manual';

    const proofOfPayment = this.state.paymentDetail.proofOfPayment || '';

    const isProjectEditable = this.state.paymentDetail.status !== 'complete' && this.state.paymentDetail.receiptUrl;

    const RenderReceiptBtn = () => {
      const url = this.state.receiptUrl;
      if (!url) return null;
      // Disable while a re-issue / silent re-issue is in flight so admins
      // don't open the soon-to-be-stale PDF mid-update.
      if (this.state.issuingReceipt) {
        return (
          <span
            className="AccountOverview-sheetBtnOutline AccountOverview-sheetBtnOutline--disabled"
            aria-disabled="true"
          >
            {textObj.ReceiptBtn || 'Receipt'}
          </span>
        );
      }
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="AccountOverview-sheetBtnOutline"
        >
          {textObj.ReceiptBtn || 'Receipt'}
        </a>
      );
    };

    const onBackPaymentDetailBtn = () => {
      this.setState({
        paymentDetail: {},
      });
      this.props.onBackBtn();
    };

    const forceIssueReceiptBtn = () => {
      const isReissuance = !!this.state.receiptUrl && this.state.paymentDetail.receiptStale === true;

      let confirmStatement;

      if (isReissuance) {
        confirmStatement =
          textObj.reissueReceiptWarning ||
          "This will void the current receipt and issue a new one with the updated details. Proceed?";
      } else if (this.state.paymentDetail.status === "complete") {
        confirmStatement =
          textObj.forceIssueReceiptWarning["complete"];
      } else if (this.state.paymentDetail.status === "processing") {
        confirmStatement =
          textObj.forceIssueReceiptWarning["processing"];
      }

      if (window.confirm(confirmStatement)) {
        this.setState({
          issuingReceipt: true,
        });

        simpleFetch("/user/payments/issueReceipt", "POST", {
          objectId: data._id,
        }).then((res) => {

          if (res.body.receiptLink) {
            this.setState((prevState) => {
              let paymentDetail = Object.assign({}, prevState.paymentDetail);
              paymentDetail.organizationReceiptId =
                res.body.organizationReceiptId;
              paymentDetail.receiptStale = false;
              paymentDetail.receiptHistory = res.body.receiptHistory || paymentDetail.receiptHistory || [];
              return { paymentDetail };
            });

            this.setState({
              receiptUrl: res.body.receiptLink,
              issuingReceipt: false,
            });
          }
        });
      }
    };

    const silentReissueReceiptBtn = () => {
      const confirmStatement =
        textObj.silentReissueWarning ||
        "This will void the current receipt and generate a new PDF. The donor will not be notified by email. Proceed?";

      if (window.confirm(confirmStatement)) {
        this.setState({ issuingReceipt: true });

        simpleFetch("/user/payments/issueReceipt", "POST", {
          objectId: data._id,
          skipEmail: true,
        }).then((res) => {
          if (res.body.receiptLink) {
            this.setState((prevState) => {
              let paymentDetail = Object.assign({}, prevState.paymentDetail);
              paymentDetail.organizationReceiptId =
                res.body.organizationReceiptId;
              paymentDetail.receiptStale = false;
              paymentDetail.receiptHistory = res.body.receiptHistory || paymentDetail.receiptHistory || [];
              return { paymentDetail };
            });

            this.setState({
              receiptUrl: res.body.receiptLink,
              issuingReceipt: false,
            });
          } else {
            this.setState({ issuingReceipt: false });
          }
        });
      }
    };

    const resendReceiptBtn = () => {
      const confirmStatement =
        textObj.resendReceiptWarning ||
        "This will email the current receipt PDF to the donor again. Proceed?";

      if (window.confirm(confirmStatement)) {
        this.setState({ issuingReceipt: true });

        simpleFetch("/user/payments/issueReceipt", "POST", {
          objectId: data._id,
          resendOnly: true,
        }).then((res) => {
          // Resend doesn't change receiptUrl / receiptHistory; just acknowledge.
          this.setState({ issuingReceipt: false });
        });
      }
    };

    const RenderForceIssueReceiptBtn = () => {
      const url = this.state.receiptUrl;
      const { paymentDetail } = this.state;
      const canManagePayments = this.canManageManualPayments();

      if (paymentDetail.PaymentMode === 'PayMe' &&
          (paymentDetail.paymeVerification !== 'verified' || paymentDetail.paymeFeeStatus !== 'confirmed' ||
            ['pending', 'dispatching'].includes(paymentDetail.paymeReceiptDelivery))) {
        return null;
      }
      if (paymentDetail.stripeFeeStatus && paymentDetail.stripePaymentReviewAt) return null;
      const stripeDonationReceipt = paymentDetail.stripeReceiptBasis === 'donation' &&
        paymentDetail.coverFee === false && paymentDetail.stripePaymentVerifiedAt;
      if (paymentDetail.stripeFeeStatus && ((!stripeDonationReceipt && paymentDetail.stripeFeeStatus !== 'confirmed') ||
          ['pending', 'dispatching'].includes(paymentDetail.stripeReceiptDelivery))) return null;
      const hasReceiptNumber = paymentDetail.organizationReceiptId && paymentDetail.organizationReceiptId.trim() !== '';
      const isManualPmt = paymentDetail.PaymentMode === 'Manual';
      const isManualPaymentEligibleForReceipt = isManualPmt &&
                                              paymentDetail.status === "complete" &&
                                              (!hasReceiptNumber || !url);

      // Show re-issue button if receipt exists and is stale (Premium only)
      const isReissuance = url && paymentDetail.receiptStale === true;

      if (isReissuance && canManagePayments) {
        if (this.state.issuingReceipt) {
          return (
            <div className="AccountOverview-sheetBtnPrimary AccountOverview-sheetBtnPrimary--loading">
              {textObj.reissueAndSend || "Re-issue and Send"}
            </div>
          );
        }
        return (
          <div
            className="AccountOverview-sheetBtnPrimary"
            onClick={forceIssueReceiptBtn}
          >
            {textObj.reissueReceipt || "Re-issue Receipt"}
          </div>
        );
      }

      // Original first-time issuance logic
      if (
        (!isManualPmt && url) ||
        paymentDetail.status === "error" ||
        paymentDetail.status === "scheduled" ||
        paymentDetail.status === "refund" ||
        paymentDetail.status === "dispute" ||
        paymentDetail.status === "reauthenticate" ||
        (isManualPmt && !isManualPaymentEligibleForReceipt)
      ) {
        return null;
      }

      if (this.state.issuingReceipt === false) {
        return (
          <div
            className="AccountOverview-sheetBtnPrimary"
            onClick={forceIssueReceiptBtn}
          >
            {textObj.issueReceipt}
          </div>
        );
      } else {
        return (
          <div className="AccountOverview-sheetBtnPrimary AccountOverview-sheetBtnPrimary--loading">
            {textObj.issueReceipt}
          </div>
        );
      }
    };

    const RenderChangeStatusBtn = () => {
      const canManagePayments = this.canManageManualPayments();
      const upgradeHint =
        this.props.upgradeHint ||
        (this.props.textObj && this.props.textObj.upgradeOfflinePaymentsHint) ||
        "Upgrade to Premium to update Offline Payments";
      const {
        showApprovalDropdown,
        showDeclineInput,
        paymentDetail: { status },
      } = this.state;

      const dropdownClassName = `AccountOverview-approvalDropdown${canManagePayments ? "" : " AccountOverview-approvalDropdown--disabled"}`;
      const buttonClassName = `AccountOverview-approvalBtn${canManagePayments ? "" : " AccountOverview-approvalBtn--disabled"}`;
      const disabledAttributes = canManagePayments
        ? {}
        : {
          "data-tooltip": upgradeHint,
          title: upgradeHint,
          "aria-disabled": "true",
        };

      if (!canManagePayments) {
        return (
          <div
            className={dropdownClassName}
            {...disabledAttributes}
          >
            <div className={buttonClassName}>
              <div className="AccountOverview-approvalIcn" />
            </div>
          </div>
        );
      }

      if (showDeclineInput) {
        // Show decline interface
        return (
          <div
            className={dropdownClassName}
            ref={this.statusDropdownRef}
          >
            <div className={buttonClassName}>
              <div className="AccountOverview-approvalIcn" />
            </div>

            <div className="AccountOverview-dropdownContent">
              <div 
                className="AccountOverview-dropdownItem" 
                onClick={this.handleDeclineClick}
              >
                Back
              </div>

              <div
                className="AccountOverview-textAreaFieldContainer dropdown"
              >
                <textarea
                  key="decline-textarea"
                  ref={this.declineTextareaRef}
                  className="AccountOverview-textAreaField"
                  placeholder="Decline Message (optional)"
                />
              </div>

              <div 
                className="AccountOverview-dropdownItem send" 
                onClick={this.handleSendDecline}
              >
                Send
              </div>
            </div>
          </div>
        );
      }

      // Show normal dropdown interface
      return (
        <div
          className={dropdownClassName}
          ref={this.statusDropdownRef}
        >
          <div
            className={buttonClassName}
            onClick={this.handleToggleApprovalDropdown}
          >
            <div className="AccountOverview-approvalIcn" />
          </div>

          {showApprovalDropdown && (
            <div className="AccountOverview-dropdownContent">
              {status === 'pending_for_confirmation' && (
                <Fragment>
                  <div 
                    className="AccountOverview-dropdownItem" 
                    onClick={() => this.handleChangeStatus('approved')}
                  >
                    {textObj.approveBtn || "Approve"}
                  </div>
                  <div 
                    className="AccountOverview-dropdownItem receipt" 
                    onClick={() => this.handleChangeStatus('approved', true)}
                  >
                    {textObj.approveAndSendReceiptBtn || "Approve and Send Receipt"}
                  </div>
                </Fragment>
              )}

              {(status === 'pending_for_confirmation' || status === 'complete') && (
                <div 
                  className="AccountOverview-dropdownItem decline" 
                  onClick={this.handleDeclineClick}
                >
                  {textObj.declineBtn || "Decline"}
                </div>
              )}

              {(status === 'error' || status === 'complete') && (
                <div 
                  className="AccountOverview-dropdownItem" 
                  onClick={() => this.handleChangeStatus('pending')}
                >
                  {textObj.pendingBtn || "Pending"}
                </div>
              )}
            </div>
          )}
        </div>
      );
    };

    const RenderEditBtn = () => {
      const canManagePayments = this.canManageManualPayments();
      const upgradeHint = this.props.upgradeHint || (this.props.textObj && this.props.textObj.upgradeOfflinePaymentsHint) || 'Upgrade to Premium to update Offline Payments';
      const className = `AccountOverview-editBtn${canManagePayments ? '' : ' AccountOverview-editBtn--disabled'}`;
      const iconClassName = `AccountOverview-editIcn${canManagePayments ? '' : ' AccountOverview-editIcn--disabled'}`;

      return (
        <div
          className={className}
          onClick={canManagePayments ? this.handleToggleEditMode : undefined}
          data-tooltip={canManagePayments ? undefined : upgradeHint}
          title={canManagePayments ? undefined : upgradeHint}
          aria-disabled={canManagePayments ? undefined : 'true'}
        >
          <div className={iconClassName} />
        </div>
      );
    }

    const RenderEditModeBtn = () => {
      return (
        <Fragment>
          <div
            className="AccountOverview-sheetBtnOutline"
            onClick={this.handleToggleEditMode}
          >
            {textObj.cancelBtn || "Cancel"}
          </div>
          <div
            className="AccountOverview-sheetBtnPrimary"
            onClick={this.handleSaveBtn}
          >
            {textObj.saveBtn || "Save"}
          </div>
        </Fragment>
      );
    }

    const RenderDeleteBtn = () => {
      const paymentId = this.state.paymentDetail.id;

      return (
        <div
          className="AccountOverview-deleteBtn"
          onClick={() => this.props.onDeleteBtn(paymentId)}
        >
          <div className="AccountOverview-deleteIcn" />
        </div>
      );
    }

    let statusColor = {
      background: "#FFFFFF",
      color: "#FFFFFF",
    };

    if (this.state.paymentDetail.status === "complete") {
      statusColor = {
        background: "#DDF6C7",
        color: "#306A22",
      };
    }

    if (this.state.paymentDetail.status === "error") {
      statusColor = {
        background: "#FBE8F2",
        color: "#A4233F",
      };
    }

    if (this.state.paymentDetail.status === "processing") {
      statusColor = {
        background: "#E3FFFE",
        color: "#2FCCD3",
      };
    }

    if (this.state.paymentDetail.status === "scheduled" || this.state.paymentDetail.status === "pending_for_confirmation" || this.state.paymentDetail.status === "pending") {
      statusColor = {
        background: "#D6F4F5",
        color: "#306ED7",
      };
    }

    if (isManualPayment && (this.state.paymentDetail.status === "pending_for_confirmation" || this.state.paymentDetail.status === "pending")) {
      statusColor = {
        background: "#FFF3E0",
        color: "#E65100",
      };
    }

    if (this.state.paymentDetail.status === "reauthenticate") {
      statusColor = {
        background: "#FFF3E0",
        color: "#E65100",
      };
    }

    if (this.state.paymentDetail.status === 'expired') {
      statusColor = { background: '#eef2f6', color: '#64748b' };
    }

    const grossAmount = Number(data.amount || 0);
    const processingFeeValue = Number(data.processingFee || 0);
    const serviceFeeValue = Number(data.serviceFee || 0);
    const totalFees = Number(
      typeof data.transactionFee === "number"
        ? data.transactionFee
        : processingFeeValue + serviceFeeValue
    );
    const netAmount = data.net != null ? Number(data.net) : Math.max(0, grossAmount - totalFees);
    // Use the captured gross for enrolled direct charges. Basket legs and
    // destination charges preserve their original amount despite platform costs.
    const chargedAmount = data.stripeFeeStatus ? stripeGross(data) :
      data.PaymentMode === 'PayMe' ? paymeGross(data) : Math.max(grossAmount, netAmount + totalFees);
    const statusLabel = this.mapPaymentStatusText(this.state.paymentDetail.status);
    const updatedLabel = data.updatedAt ? new Date(data.updatedAt).toLocaleString() : "";
    const scheduledLabel = data.scheduledDate ? new Date(data.scheduledDate).toLocaleString() : "";
    const isScheduled = this.state.paymentDetail.status === "scheduled";
    const displayReceiptId = paymentId;
    const donorFullName = data.donorOrganizationName || [data.donorFirstName, data.donorLastName].map(s => (s || '').trim()).filter(Boolean).join(" ") || '';
    const sectionsConfig = textObj.sections || {};
    const sectionTitles = {
      summary: sectionsConfig.summary || textObj.summaryTitle || "Payment summary",
      donor: sectionsConfig.donor || "Donor details",
      method: sectionsConfig.method || "Payment method",
      metadata: sectionsConfig.metadata || "Additional details",
    };
    const sectionSubtitles = {
      summary: sectionsConfig.summarySubtitle || "",
      donor: sectionsConfig.donorSubtitle || "",
      method: sectionsConfig.methodSubtitle || "",
      metadata: sectionsConfig.metadataSubtitle || "",
    };

    const lastUpdatedCopy = (textObj.info && textObj.info.lastUpdated) || "Last updated";
    const scheduledForCopy = (textObj.info && textObj.info.scheduledFor) || "Scheduled for";

    const formatMoney = (value, decimalScale = 2) => (
      <NumericFormat
        decimalSeparator="."
        displayType="text"
        valueIsNumericString={true}
        value={value || 0}
        allowNegative={false}
        thousandSeparator={true}
        prefix={"$"}
        fixedDecimalScale={true}
        decimalScale={decimalScale}
      />
    );

    const renderError = (field) => {
      const { errorMsg } = this.state;
  
      if (errorMsg[field.id]) {
        return (
          <div className="AccountOverview-errorContainer">
            {renderErrorMsg(errorMsg[field.id])}
          </div>
        );
      }
  
      return null;
    }

    const renderInputInfoText = field => {
      const { isEditMode } = this.state;

      if (isEditMode && (field.archived || field.deleted)) {
        let infoText = 'This custom field is archived';

        if (field.deleted) {
          infoText = 'This custom field is deleted';
        }

        return (
          <div className="AccountOverview-inputInfoText">
            {infoText}
          </div>
        );
      }

      return null;
    };

    const renderCustomFields = () => {
      const customFields = this.state.formData.filter(f => f.isCustomField);

      if (customFields && customFields.length > 0) {
        return customFields.map((field) => {
          if (field.type === 'textarea') {
            return renderInputTextarea(field.id);
          } else if (field.type === 'yesno') {
            return renderInputYesNo(field.id);
          } else if (field.type === 'dropdown') {
            return renderInputCustomDropdown(field.id);
          } else if (field.type === 'address') {
            return renderInputAddress(field.id);
          } else {
            return renderInputText(field.id);
          }
        });
      }

      return null;
    }

    const renderInputYesNo = (id) => {
      const { formData, isEditMode } = this.state;
      const field = formData.find((f) => f.id === id);
      if (!field) return null;

      const classDisabled = field.deleted || field.archived ? 'disabled' : '';
      const isYes = field.value === 'yes';

      return (
        <tr key={field.id} className="AccountOverview-paymentRow">
          <th>
            <div className="AccountOverview-description">{field.label}</div>
          </th>
          <th>
            {isEditMode ? (
              <div
                className={`AccountOverview-yesNoToggle ${classDisabled}`}
                onClick={() => {
                  if (!field.archived && !field.deleted) {
                    this.handleInputChange({ target: { value: isYes ? 'no' : 'yes' } }, field);
                  }
                }}
              >
                <div className={`AccountOverview-yesNoOption${isYes ? ' AccountOverview-yesNoOption--active' : ''}`}>{this.props.language === 'zh' || this.props.language === 'cn' ? '是' : 'Yes'}</div>
                <div className={`AccountOverview-yesNoOption${!isYes ? ' AccountOverview-yesNoOption--active' : ''}`}>{this.props.language === 'zh' || this.props.language === 'cn' ? '否' : 'No'}</div>
              </div>
            ) : (
              <div className="AccountOverview-dataInput">
                {this.props.language === 'zh' || this.props.language === 'cn' ? (isYes ? '是' : '否') : (isYes ? 'Yes' : 'No')}
              </div>
            )}
            {renderInputInfoText(field)}
          </th>
        </tr>
      );
    }

    const renderInputCustomDropdown = (id) => {
      const { formData, isEditMode } = this.state;
      const field = formData.find((f) => f.id === id);
      if (!field) return null;

      const classDisabled = field.deleted || field.archived ? 'disabled' : '';

      return (
        <tr key={field.id} className="AccountOverview-paymentRow">
          <th>
            <div className="AccountOverview-description">{field.label}</div>
          </th>
          <th>
            {isEditMode ? (
              <div className={`AccountOverview-inputFieldContainer ${classDisabled}`}>
                <input
                  type="text"
                  className={`AccountOverview-inputField ${classDisabled}`}
                  value={field.value}
                  disabled={field.archived || field.deleted}
                  onChange={(event) => this.handleInputChange(event, field)}
                />
              </div>
            ) : (
              <div className="AccountOverview-dataInput">
                {field.value}
              </div>
            )}
            {renderInputInfoText(field)}
          </th>
        </tr>
      );
    }

    const renderInputAddress = (id) => {
      const { formData, isEditMode } = this.state;
      const field = formData.find((f) => f.id === id);
      if (!field) return null;

      const classDisabled = field.deleted || field.archived ? 'disabled' : '';
      let parsed = {};
      try { parsed = JSON.parse(field.value || '{}'); } catch (e) { parsed = {}; }

      const isHK = (parsed.country || 'Hong Kong') === 'Hong Kong';
      const districts = parsed.region ? getDistrictsByRegion(parsed.region) : [];

      const intlFields = [
        { key: 'address1', placeholder: 'Address Line 1' },
        { key: 'address2', placeholder: 'Address Line 2' },
        { key: 'city', placeholder: 'City' },
        { key: 'state', placeholder: 'State / Province' },
        { key: 'postalCode', placeholder: 'Postal Code' },
      ];

      const hkTextFields = [
        { key: 'street', placeholder: 'Street No. & Name' },
        { key: 'building', placeholder: 'Building Name' },
        { key: 'block', placeholder: 'Block / Tower' },
        { key: 'floor', placeholder: 'Floor' },
        { key: 'flat', placeholder: 'Flat / Unit' },
      ];

      const activeFields = isHK ? hkTextFields : intlFields;
      const countryDisplay = parsed.country || 'Hong Kong';

      // Build display value
      let displayParts;
      if (isHK) {
        const regionObj = hkRegions.find(r => r.id === parsed.region);
        const districtObj = districts.find(d => d.id === parsed.district);
        displayParts = [
          parsed.flat && `Flat ${parsed.flat}`,
          parsed.floor && `${parsed.floor}/F`,
          parsed.block,
          parsed.building,
          parsed.street,
          districtObj && districtObj.name_en,
          regionObj && regionObj.name_en,
          regionObj && regionObj.id !== 'hk' && 'HONG KONG',
        ];
      } else {
        displayParts = [
          ...intlFields.map(sub => parsed[sub.key]),
          countryDisplay,
        ];
      }
      const displayValue = displayParts.filter(Boolean).join(', ');

      const handleChange = (subKey, subValue) => {
        let updated;
        if (subKey === 'country') {
          updated = { country: subValue };
        } else if (subKey === 'region') {
          updated = { ...parsed, region: subValue, district: '' };
        } else {
          updated = { ...parsed, [subKey]: subValue };
        }
        this.handleInputChange({ target: { value: JSON.stringify(updated) } }, field);
      };

      return (
        <tr key={field.id} className="AccountOverview-paymentRow">
          <th>
            <div className="AccountOverview-description">{field.label}</div>
          </th>
          <th>
            {isEditMode ? (
              <div className={classDisabled}>
                <div className={`AccountOverview-inputFieldContainer ${classDisabled}`} style={{ marginBottom: '6px' }}>
                  <select
                    className={`AccountOverview-inputField ${classDisabled}`}
                    value={parsed.country || 'Hong Kong'}
                    disabled={field.archived || field.deleted}
                    onChange={(e) => handleChange('country', e.target.value)}
                  >
                    {countryList.map(c => (
                      <option key={c.value} value={c.value}>{getLabel(c, this.props.language)}</option>
                    ))}
                  </select>
                </div>
                {isHK ? (
                  <Fragment>
                    <div className={`AccountOverview-inputFieldContainer ${classDisabled}`} style={{ marginBottom: '6px' }}>
                      <select
                        className={`AccountOverview-inputField ${classDisabled}`}
                        value={parsed.region || ''}
                        disabled={field.archived || field.deleted}
                        onChange={(e) => handleChange('region', e.target.value)}
                      >
                        <option value="">-- Select Region --</option>
                        {hkRegions.map(r => (
                          <option key={r.id} value={r.id}>{getLabel(r, this.props.language)}</option>
                        ))}
                      </select>
                    </div>
                    <div className={`AccountOverview-inputFieldContainer ${classDisabled}`} style={{ marginBottom: '6px' }}>
                      <select
                        className={`AccountOverview-inputField ${classDisabled}`}
                        value={parsed.district || ''}
                        disabled={field.archived || field.deleted || !parsed.region}
                        onChange={(e) => handleChange('district', e.target.value)}
                      >
                        <option value="">-- Select District --</option>
                        {districts.map(d => (
                          <option key={d.id} value={d.id}>{getLabel(d, this.props.language)}</option>
                        ))}
                      </select>
                    </div>
                  </Fragment>
                ) : null}
                {activeFields.map(sub => (
                  <div key={sub.key} className={`AccountOverview-inputFieldContainer ${classDisabled}`} style={{ marginBottom: '6px' }}>
                    <input
                      type="text"
                      className={`AccountOverview-inputField ${classDisabled}`}
                      placeholder={sub.placeholder}
                      value={parsed[sub.key] || ''}
                      disabled={field.archived || field.deleted}
                      onChange={(e) => handleChange(sub.key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="AccountOverview-dataInput">
                {displayValue || '-'}
              </div>
            )}
            {renderInputInfoText(field)}
          </th>
        </tr>
      );
    }

    const renderInputText = (id, isEditable = true) => {
      const { formData, isEditMode } = this.state;
      const { textObj } = this.props;

      const field = formData.find((field) => field.id === id);

      if (field) {
        const classDisabled = field.deleted || field.archived ? 'disabled' : '';
        
        // Use localized field labels when available
        let label = field.label;
        
        // Map field IDs to info keys from language file
        const fieldToInfoMapping = {
          'firstName': 'donorFirstName',  // Use specific field names for first and last name
          'lastName': 'donorLastName',   // Use specific field names for first and last name
          'email': 'donorEmail',
          'phone': 'phoneNumber',
          'note': 'memo',
          'projectId': 'projectName',
          'paymentType': 'transactionType',
          'paymentName': 'cardData',
          'status': 'status',
          'amount': 'amount',
          'transactionFee': 'fee',
          'net': 'net'
        };
        
        if (fieldToInfoMapping[field.id] && textObj.info[fieldToInfoMapping[field.id]]) {
          label = textObj.info[fieldToInfoMapping[field.id]];
        }

        return (
          <tr key={field.id} className="AccountOverview-paymentRow">
            <th>
              <div className="AccountOverview-description">
                {label}
              </div>
            </th>
            <th>
              {isEditable && isEditMode ? (
                <div
                  className={`AccountOverview-inputFieldContainer ${classDisabled}`}
                >
                  <input
                    type="text"
                    className={`AccountOverview-inputField ${classDisabled}`}
                    placeholder={field.placeholder}
                    value={field.value}
                    disabled={field.archived || field.deleted}
                    onChange={(event) => this.handleInputChange(event, field)}
                  />
                </div>
              ) : (
                <div className="AccountOverview-dataInput">
                  {field.value}
                </div>
              )}

              {renderInputInfoText(field)}
              {renderError(field)}
            </th>
          </tr>
        );
      }

      return null;
    };

    const renderInputTextarea = (id, isEditable = true) => {
      const { formData, isEditMode } = this.state;
      const { textObj } = this.props;

      const field = formData.find((field) => field.id === id);

      if (field) {
        const classDisabled = field.deleted || field.archived ? 'disabled' : '';
        
        // Use localized field labels when available
        let label = field.label;
        
        // Map field IDs to info keys from language file
        const fieldToInfoMapping = {
          'note': 'memo',
          'firstName': 'donorName',
          'lastName': 'donorName',
          'email': 'donorEmail',
          'phone': 'phoneNumber',
          'projectId': 'projectName',
          'paymentType': 'transactionType',
          'paymentName': 'cardData',
          'status': 'status',
          'amount': 'amount',
          'transactionFee': 'fee',
          'net': 'net'
        };
        
        if (fieldToInfoMapping[field.id] && textObj.info[fieldToInfoMapping[field.id]]) {
          label = textObj.info[fieldToInfoMapping[field.id]];
        }

        return (
          <tr key={field.id} className="AccountOverview-paymentRow">
            <th>
              <div className="AccountOverview-description">
                {label}
              </div>
            </th>
            <th>
              {isEditable && isEditMode ? (
                <div
                  className={`AccountOverview-textAreaFieldContainer ${classDisabled}`}
                >
                  <textarea
                    type="text"
                    className={`AccountOverview-textAreaField ${classDisabled}`}
                    placeholder={field.placeholder}
                    value={field.value}
                    onChange={(event) => this.handleInputChange(event, field)}
                  />
                </div>
              ) : (
                <div className="AccountOverview-dataInput">
                  {field.value}
                </div>
              )}

              {renderInputInfoText(field)}
              {renderError(field)}
            </th>
          </tr>
        );
      }

      return null;
    };

    const renderInputPhone = (id, isEditable = true) => {
      const { formData, isEditMode } = this.state;
      const { textObj } = this.props;

      const field = formData.find((field) => field.id === id);

      if (field) {
        // Use localized field labels when available
        let label = field.label;
        
        // Map field IDs to info keys from language file
        const fieldToInfoMapping = {
          'phone': 'phoneNumber',
          'firstName': 'donorFirstName',  // Use specific field names for first and last name
          'lastName': 'donorLastName',   // Use specific field names for first and last name
          'email': 'donorEmail',
          'note': 'memo',
          'projectId': 'projectName',
          'paymentType': 'transactionType',
          'paymentName': 'cardData',
          'status': 'status',
          'amount': 'amount',
          'transactionFee': 'fee',
          'net': 'net'
        };
        
        if (fieldToInfoMapping[field.id] && textObj.info[fieldToInfoMapping[field.id]]) {
          label = textObj.info[fieldToInfoMapping[field.id]];
        }
        
        return (
          <tr key={field.id} className="AccountOverview-paymentRow">
            <th>
              <div className="AccountOverview-description">
                {label}
              </div>
            </th>
            <th>
              {isEditable && isEditMode ? (
                <div className="AccountOverview-inputFieldContainer">
                  <div className="AccountOverview-phoneField">
                    <PhoneInput
                      placeholder={field.placeholder}
                      value={field.value}
                      defaultCountry={"HK"}
                      flags={flags}
                      onChange={(phone) => this.handleInputChange({ target: { value: phone } }, field)}
                    />
                  </div>
                </div>
              ) : (
                <div className="AccountOverview-dataInput">
                  {field.value}
                </div>
              )}

              {renderError(field)}
            </th>
          </tr>
        );
      }

      return null;
    }

    const renderInputDropdown = (id, isEditable = true) => {
      const { formData, isEditMode } = this.state;
      const { textObj } = this.props;

      const field = formData.find((field) => field.id === id);

      if (field) {
        let textView = (
          <div className="AccountOverview-dataInput">
            {field.valueText}
          </div>
        );

        if (field.id === 'status') {
          textView = (
            <div
              className="AccountOverview-statusContainer"
              style={statusColor}
            >
              {field.valueText}
            </div>
          );
        }

        // Use localized field labels when available
        let label = field.label;
        
        // Map field IDs to info keys from language file
        const fieldToInfoMapping = {
          'projectId': 'projectName',
          'paymentType': 'transactionType',
          'paymentName': 'cardData',
          'status': 'status',
          'amount': 'amount',
          'transactionFee': 'fee',
          'net': 'net',
          'firstName': 'donorFirstName',  // Use specific field names for first and last name
          'lastName': 'donorLastName',   // Use specific field names for first and last name
          'email': 'donorEmail',
          'phone': 'phoneNumber',
          'note': 'memo'
        };
        
        if (fieldToInfoMapping[field.id] && textObj.info[fieldToInfoMapping[field.id]]) {
          label = textObj.info[fieldToInfoMapping[field.id]];
        } else if (field.id === 'status' && textObj.status && textObj.status.status) {
          label = textObj.status.status;
        }

        const classDisabled = field.disabled ? 'disabled' : '';
        const valueTextOnEdit = field.options.find((option) => option.value === field.value);
        const dropdownDisplayText = valueTextOnEdit ? valueTextOnEdit.label : (field.valueText || '');

        return (
          <tr key={field.id} className="AccountOverview-paymentRow">
            <th>
              <div className="AccountOverview-description">
                {label}
              </div>
            </th>
            <th>
              {isEditable && isEditMode ? (
                <div
                  className={`AccountOverview-inputFieldContainer ${classDisabled}`}
                >
                  <div
                    className={`AccountOverview-formDropdownBtn ${classDisabled}`}
                  >
                    <div className="AccountOverview-formDropdownText">
                      {dropdownDisplayText}
                    </div>
                    <div className="AccountOverview-formDropdownIcn" />
                    <select
                      className={`AccountOverview-formDropdownSelect ${classDisabled}`}
                      value={field.value}
                      onChange={(event) => this.handleInputChange(event, field)}
                      disabled={field.disabled}
                    >
                      {field.options.map((option) => (
                        <option key={option.value} value={option.value} disabled={option.disabled || false}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : textView}

              {renderError(field)}
            </th>
          </tr>
        );
      }

      return null;
    }

    const renderInputDecimal = (id, isEditable = true, disabled = false) => {
      const { formData, isEditMode } = this.state;
      const { textObj } = this.props;

      const field = formData.find((field) => field.id === id);

      if (field) {
        const classDisabled = disabled ? 'disabled' : '';
        
        // Use localized field labels when available
        let label = field.label;
        
        // Map field IDs to info keys from language file
        const fieldToInfoMapping = {
          'amount': 'amount',
          'transactionFee': 'fee',
          'net': 'net',
          'firstName': 'donorFirstName',  // Use specific field names for first and last name
          'lastName': 'donorLastName',   // Use specific field names for first and last name
          'email': 'donorEmail',
          'phone': 'phoneNumber',
          'note': 'memo',
          'projectId': 'projectName',
          'paymentType': 'transactionType',
          'paymentName': 'cardData',
          'status': 'status'
        };
        
        if (fieldToInfoMapping[field.id] && textObj.info[fieldToInfoMapping[field.id]]) {
          label = textObj.info[fieldToInfoMapping[field.id]];
        }

        return (
          <tr key={field.id} className="AccountOverview-paymentRow">
            <th>
              <div className="AccountOverview-description">
                {label}
              </div>
            </th>
            <th>
              {isEditable && isEditMode ? (
                <div
                  className={`AccountOverview-inputFieldContainer ${classDisabled}`}
                >
                  <div className="AccountOverview-currencySign">HKD</div>
                  <NumericFormat
                    pattern="[0-9]*"
                    inputMode="decimal"
                    decimalSeparator="."
                    className={`AccountOverview-dollarField ${classDisabled}`}
                    valueIsNumericString={true}
                    value={field.value}
                    onValueChange={(values) => this.handleInputChange({ target: { value: values.value } }, field)}
                    allowNegative={false}
                    thousandSeparator={true}
                    prefix={"$"}
                    fixedDecimalScale={true}
                    decimalScale={2}
                    disabled={disabled}
                  />
                </div>
              ) : (
                <div className="AccountOverview-dataInput">
                  HK
                  <NumericFormat
                    decimalSeparator="."
                    displayType="text"
                    valueIsNumericString={true}
                    value={field.value}
                    allowNegative={false}
                    thousandSeparator={true}
                    prefix={"$"}
                    fixedDecimalScale={true}
                    decimalScale={2}
                  />
                </div>
              )}

              {renderError(field)}
            </th>
          </tr>
        );
      }

      return null;
    };

    const renderInputReadonly = (id) => {
      const { formData } = this.state;
      const field = formData.find((field) => field.id === id);

      if (field) {
        const displayValue = field.value
          ? new Date(field.value).toLocaleString()
          : '-';

        return (
          <tr key={field.id} className="AccountOverview-paymentRow">
            <th>
              <div className="AccountOverview-description">
                {field.label}
              </div>
            </th>
            <th>
              <div className="AccountOverview-dataInput">
                {displayValue}
              </div>
            </th>
          </tr>
        );
      }

      return null;
    };

    const renderLinkProofOfPayment = () => {
      const { loadingProofOfPayment } = this.state;

      if (isManualPayment && proofOfPayment !== '') {
        let textClass = 'link';
        let textAction = this.handleViewProofOfPayment;
        let text = 'View';

        if (loadingProofOfPayment) {
          textClass = '';
          textAction = () => {};
          text = 'Loading..';
        }

        const valueClassNames = ['AccountOverview-dataInput'];
        if (textClass === 'link') {
          valueClassNames.push('AccountOverview-paymentValueLink');
        }

        return (
          <tr className="AccountOverview-paymentRow">
            <th>
              <div className="AccountOverview-description">
                {textObj.info["proofOfPayment"]}
              </div>
            </th>
            <th>
              <div
                className={`AccountOverview-dataInput ${textClass === 'link' ? 'AccountOverview-viewProofLink' : 'AccountOverview-viewProofLoading'}`}
                onClick={textClass === 'link' ? () => textAction() : undefined}
                role={textClass === 'link' ? 'button' : undefined}
                tabIndex={textClass === 'link' ? 0 : -1}
              >
                {textClass === 'link' ? textObj.info["view"] : textObj.info["loading"]}
              </div>
            </th>
          </tr>
        );
      }

      return null;
    };

    const renderSectionHeading = (title, subtitle) => {
      if (!title) return null;
      return (
        <tr className="AccountOverview-paymentSectionRow">
          <th colSpan="2">
            <div className="AccountOverview-paymentSectionHeading">
              <div className="AccountOverview-paymentSectionTitle">{title}</div>
              {subtitle ? (
                <div className="AccountOverview-paymentSectionSubtitle">{subtitle}</div>
              ) : null}
            </div>
          </th>
        </tr>
      );
    };

    const renderFeeBreakdownSection = () => {
      if (isManualPayment) {
        return renderInputDecimal('transactionFee', isManualPayment);
      }

      const feeTotal = totalFees || processingFeeValue + serviceFeeValue;
      const hasFeeDetails = processingFeeValue > 0 || serviceFeeValue > 0;
      const breakdownButtonLabel = this.state.showFeeBreakdown
        ? ((textObj.info && textObj.info.hideFeeBreakdown) || 'Hide breakdown')
        : ((textObj.info && textObj.info.viewFeeBreakdown) || 'View breakdown');

      const feeDetails = [
        {
          key: 'processingFee',
          label: textObj.info["processingFee"]
            ? `${data.PaymentMode} ${textObj.info["processingFee"]}`
            : `${data.PaymentMode} Processing Fee`,
          value: processingFeeValue,
        },
        {
          key: 'serviceFee',
          label: textObj.info["serviceFee"]
            ? `CheckPlus ${textObj.info["serviceFee"]}`
            : "CheckPlus Platform Fee",
          value: serviceFeeValue,
        },
      ].filter((detail) => detail.value > 0);

      return (
        <Fragment>
          <tr className="AccountOverview-paymentRow">
            <th>
              <div className="AccountOverview-description">
                {textObj.info["fee"] || "Fees"}
              </div>
            </th>
            <th>
              <div className="AccountOverview-feeValueRow">
                <div className="AccountOverview-dataInput">
                  HK{formatMoney(feeTotal)}
                </div>
                {hasFeeDetails ? (
                  <button
                    type="button"
                    className="AccountOverview-feeToggleBtn"
                    onClick={this.handleToggleFeeBreakdown}
                    aria-expanded={this.state.showFeeBreakdown}
                  >
                    {breakdownButtonLabel}
                  </button>
                ) : null}
              </div>
            </th>
          </tr>

          {this.state.showFeeBreakdown && hasFeeDetails
            ? feeDetails.map((detail) => (
              <tr
                key={detail.key}
                className="AccountOverview-paymentRow AccountOverview-paymentRow--nested"
              >
                <th>
                  <div className="AccountOverview-description AccountOverview-description--muted">
                    {detail.label}
                  </div>
                </th>
                <th>
                  <div className="AccountOverview-dataInput">
                    HK{formatMoney(detail.value)}
                  </div>
                </th>
              </tr>
            ))
            : null}

          {this.state.showFeeBreakdown && !hasFeeDetails ? (
            <tr className="AccountOverview-paymentRow AccountOverview-paymentRow--nested">
              <th colSpan="2">
                <div className="AccountOverview-feeDetailNote">
                  {(textObj.info && textObj.info.noFeeBreakdown) || 'No additional fee details available'}
                </div>
              </th>
            </tr>
          ) : null}
        </Fragment>
      );
    };

    const createdLabel = data.createdAt ? new Date(data.createdAt).toLocaleString() : "";

    const stripeChecking = this.state.stripeCheckPaymentId === data.id && this.state.stripeChecking;
    const paymeChecking = this.state.paymeCheckPaymentId === data.id && this.state.paymeChecking;
    const stripeCheckResult = (this.state.stripeCheckPaymentId === data.id ? this.state.stripeCheckResult : null) ||
      (data.stripeManualReview?.changes?.length ? 'differences' : null);
    const stripeNeedsReview = ['differences', 'review'].includes(stripeCheckResult);
    const paymeVerified = isPaymeVerified(data);
    let paymeResult = (this.state.paymeCheckPaymentId === data.id ? this.state.paymeCheckResult : null) ||
      (data.paymeManualReview?.changes?.length ? 'differences' : null);
    // A successful response cannot override newer payment/receipt review flags.
    if (paymeResult === 'verified' && !paymeVerified) {
      paymeResult = data.paymeManualReview?.changes?.length ? 'differences' :
        data.status !== 'complete' || data.paymeSyncExhausted || data.paymeVerification !== 'verified' ||
          data.paymeFeeStatus !== 'confirmed' || data.paymeReview?.status === 'required' ? 'review' :
          data.paymeReceiptCorrection?.status === 'review' || data.paymeReceiptDelivery === 'review' ? 'receipt_review' :
            (data.paymeReceiptCorrection && data.paymeReceiptCorrection.status !== 'completed') ||
              ['pending', 'dispatching'].includes(data.paymeReceiptDelivery) ? 'receipt_pending' :
              data.receiptStale ? 'receipt_stale' : 'review';
    }
    const paymeNeedsReview = ['differences', 'review', 'receipt_review', 'receipt_stale', 'refresh_failed'].includes(paymeResult);
    const paymeNeutral = ['matched', 'receipt_pending', 'expired'].includes(paymeResult);
    const paymeResultLabel = { differences: 'differencesLabel', review: 'verificationReviewLabel', matched: 'matchedLabel',
      receipt_pending: 'receiptPreparingLabel', receipt_review: 'receiptReviewLabel', receipt_stale: 'receiptStaleLabel',
      refresh_failed: 'refreshNeededLabel', expired: 'expired' }[paymeResult] || (paymeVerified ? 'verifiedLabel' : 'verificationReviewLabel');
    const renderObj = () => {
      return (
        <div>
          {/* Hero Section */}
          <div className="AccountOverview-heroSection">
            {data.PaymentMode === 'Stripe' && (stripeChecking || stripeNeedsReview || isStripeVerified(data)) && <div
              className={`AccountOverview-stripeCompactLabel${stripeChecking ? ' is-checking-stripe' : stripeNeedsReview ? ' is-review' : ''}`} role="status">
              {stripeChecking ? <span aria-hidden="true" className="AccountOverview-stripeSpinner" /> : <span aria-hidden="true">{stripeNeedsReview ? '!' : '✓'}</span>}
              {stripeChecking ? stripeText(this.props.language).checkingLabel : stripeNeedsReview ?
                stripeText(this.props.language)[stripeCheckResult === 'differences' ? 'differencesLabel' : 'verificationReviewLabel'] : stripeText(this.props.language).verifiedLabel}
            </div>}
            {/* Hide the blanket PayMe success badge until sync also checks refunds. */}
            {data.PaymentMode === 'PayMe' && (paymeChecking || (paymeResult && paymeResult !== 'verified')) && <div
              className={`AccountOverview-stripeCompactLabel${paymeChecking ? ' is-checking-payme' : paymeNeedsReview ? ' is-review' : paymeNeutral ? ' is-matched' : ''}`} role="status">
              {paymeChecking ? <span aria-hidden="true" className="AccountOverview-stripeSpinner" /> : <span aria-hidden="true">{paymeNeedsReview ? '!' : paymeNeutral ? 'i' : '✓'}</span>}
              {paymeText(this.props.language)[paymeChecking ? 'checkingLabel' : paymeResultLabel]}
            </div>}
            <div className="AccountOverview-heroAmount">
              HK{formatMoney(chargedAmount)}
            </div>
            <div className="AccountOverview-heroMeta">
              {statusLabel && (
                <span className="AccountOverview-paymentStatusPill" style={statusColor}>
                  {statusLabel}
                </span>
              )}
              {displayReceiptId && (
                <span className="AccountOverview-heroReceiptId">{displayReceiptId}</span>
              )}
              {donorFullName && (
                <span className="AccountOverview-heroDonor">{donorFullName.toUpperCase()}</span>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="AccountOverview-timeline">
            <div className="AccountOverview-timelineTitle">
              {textObj.timeline || 'TIMELINE'}
            </div>
            {(() => {
              const timelineEvents = [];

              if (data.updatedAt) {
                timelineEvents.push({
                  key: 'updated',
                  date: new Date(data.updatedAt),
                  label: lastUpdatedCopy,
                  type: 'active',
                });
              }

              if (data.receiptHistory && data.receiptHistory.length > 0) {
                data.receiptHistory.forEach((entry, idx) => {
                  timelineEvents.push({
                    key: `reissue-${idx}`,
                    date: new Date(entry.reissuedAt || entry.voidedAt),
                    label: textObj.receiptReissued || 'Receipt re-issued',
                    type: 'reissue',
                  });
                });
              }

              if (data.scheduledDate) {
                timelineEvents.push({
                  key: 'scheduled',
                  date: new Date(data.scheduledDate),
                  label: textObj.info["scheduledDate"] || 'Transaction date',
                  type: 'default',
                });
              }

              if (data.createdAt) {
                timelineEvents.push({
                  key: 'created',
                  date: new Date(data.createdAt),
                  label: textObj.info["createdAt"] || 'Payment created',
                  type: 'default',
                });
              }

              timelineEvents.sort((a, b) => b.date - a.date);

              // Mark the most recent event as active
              if (timelineEvents.length > 0) {
                timelineEvents[0].type = 'active';
              }

              return timelineEvents.map((event) => (
                <div
                  className={`AccountOverview-timelineItem${event.type === 'active' ? ' AccountOverview-timelineItem--active' : ''}`}
                  key={event.key}
                >
                  <div className={`AccountOverview-timelineDot${event.type === 'reissue' ? ' AccountOverview-timelineDot--reissue' : ''}`} />
                  <div className="AccountOverview-timelineContent">
                    <div className="AccountOverview-timelineLabel">{event.label}</div>
                    <div className="AccountOverview-timelineDate">{event.date.toLocaleString()}</div>
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* Payment Details Card */}
          <div className="AccountOverview-paymentTableCard">
            <table className="AccountOverview-paymentTable">
              <tbody>
                {renderSectionHeading(sectionTitles.summary, sectionSubtitles.summary)}

                {displayReceiptId ? (
                  <tr className="AccountOverview-paymentRow">
                    <th>
                      <div className="AccountOverview-description">
                        {textObj.donationID}
                      </div>
                    </th>
                    <th>
                      <div className="AccountOverview-dataInput">{displayReceiptId}</div>
                    </th>
                  </tr>
                ) : null}

                {renderInputDropdown('projectId', isProjectEditable)}
                {renderInputDropdown('paymentType', isProjectEditable)}
                {renderInputDecimal('amount', isManualPayment)}
                {data.PaymentMode === 'PayMe' ?
                  <PaymeSettlementRows payment={data} language={this.props.language} /> :
                  data.stripeFeeStatus ? <StripeSettlementRows payment={data} language={this.props.language} /> :
                    <Fragment>{renderFeeBreakdownSection()}{renderInputDecimal('net', isManualPayment, true)}</Fragment>}

                {renderSectionHeading(sectionTitles.method, sectionSubtitles.method)}
                <tr className="AccountOverview-paymentRow">
                  <th>
                    <div className="AccountOverview-description">
                      {textObj.info["paymentMode"] || "Payment Mode"}
                    </div>
                  </th>
                  <th>
                    <div className="AccountOverview-dataInput">
                      {isManualPayment
                        ? <span className="AccountOverview-manualBadge">{textObj.paymentModeManual || 'Manual'}</span>
                        : (textObj.paymentModeAutomatic || `Automatic (${paymentMode})`)}
                    </div>
                  </th>
                </tr>
                {(lastFour || wallet || cardBrand) && !isManualPayment ? (
                  <tr className="AccountOverview-paymentRow">
                    <th>
                      <div className="AccountOverview-description">
                        {textObj.info["cardData"]}
                      </div>
                    </th>
                    <th>
                      <div className="AccountOverview-dataInput">
                        <Fragment>
                          {wallet} {cardBrand}
                          {lastFour ? <span>•••• {lastFour}</span> : null}
                        </Fragment>
                      </div>
                    </th>
                  </tr>
                ) : null}
                {isManualPayment && isValidManualPaymentName(paymentName)
                  ? renderInputDropdown('paymentName', isManualPayment)
                  : null}
                {renderLinkProofOfPayment()}

                {renderSectionHeading(sectionTitles.donor, sectionSubtitles.donor)}
                {data.donorOrganizationName ? (
                  <tr className="AccountOverview-paymentRow">
                    <th>
                      <div className="AccountOverview-description">
                        {textObj.info["organizationName"] || "Organization"}
                      </div>
                    </th>
                    <th>
                      <div className="AccountOverview-dataInput">{data.donorOrganizationName}</div>
                    </th>
                  </tr>
                ) : null}
                {((data.donorFirstName || '').trim() || this.state.isEditMode) && renderInputText('firstName')}
                {((data.donorLastName || '').trim() || this.state.isEditMode) && renderInputText('lastName')}
                {renderInputText('email')}
                {renderInputPhone('phone')}

                {renderSectionHeading(sectionTitles.metadata, sectionSubtitles.metadata)}

                {isManualPayment && renderInputReadonly('scheduledDate')}
                {renderInputReadonly('createdAt')}

                <tr className="AccountOverview-paymentRow">
                  <th>
                    <div className="AccountOverview-description">
                      {textObj.info["country"] || "Country/Region"}
                    </div>
                  </th>
                  <th>
                    <div className="AccountOverview-dataInput">{data.country}</div>
                  </th>
                </tr>

                {stripeError ? (
                  <tr className="AccountOverview-paymentRow">
                    <th>
                      <div className="AccountOverview-description">
                        {textObj.info["stripeError"] || "Stripe Error"}
                      </div>
                    </th>
                    <th>
                      <div className="AccountOverview-dataInput">{stripeError}</div>
                    </th>
                  </tr>
                ) : null}

                {!data.memo || data.memo <= 1 ? null : renderInputTextarea('note')}

                {this.state.paymentDetail.paymentReference ? (
                  <tr className="AccountOverview-paymentRow">
                    <th>
                      <div className="AccountOverview-description">
                        {textObj.info["paymentReference"] || "Payment Reference"}
                      </div>
                    </th>
                    <th>
                      <div className="AccountOverview-dataInput">{this.state.paymentDetail.paymentReference}</div>
                    </th>
                  </tr>
                ) : null}

                {!data.stripePaymentIntentId ? null : (
                  <tr className="AccountOverview-paymentRow">
                    <th>
                      <div className="AccountOverview-description">
                        {textObj.info["stripePaymentId"] || "Stripe Payment ID"}
                      </div>
                    </th>
                    <th>
                      <div className="AccountOverview-dataInput">
                        {stripePaymentIntentId}
                      </div>
                    </th>
                  </tr>
                )}

                {renderCustomFields()}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    return (
      <div className="AccountOverview-organization_container AccountOverview-paymentSheet">
        {this.state.showPDFViewer && this.state.pdfUrl && (
          <FileViewer
            url={this.state.pdfUrl}
            onClose={this.handleClosePDFViewer}
          />
        )}
        <div className="AccountOverview-info">
          <div className={`AccountOverview-sheetHeader${this.state.isEditMode ? ' AccountOverview-sheetHeader--editMode' : ''}`}>
            <div
              className="AccountOverview-sheetCloseBtn"
              onClick={onBackPaymentDetailBtn}
            >
              &times;
            </div>
            <div className="AccountOverview-sheetTitle">
              {this.state.isEditMode
                ? (textObj.editPayment || 'Edit Payment')
                : (textObj.paymentDetails || 'Payment details')}
            </div>
            <div className="AccountOverview-sheetActions">
              {this.state.isEditMode ? <RenderEditModeBtn /> : (
                <Fragment>
                  <RenderReceiptBtn />
                  <RenderForceIssueReceiptBtn />

                  <div className="AccountOverview-moreMenuContainer" ref={this.actionsMenuRef}>
                    <div
                      className="AccountOverview-moreMenuBtn"
                      onClick={this.handleToggleActionsMenu}
                    >
                      &middot;&middot;&middot;
                    </div>
                    {this.state.showActionsMenu && (
                      <div className="AccountOverview-moreMenuDropdown">
                        {data.PaymentMode === 'Stripe' && data.status === 'complete' && data.id != null && (
                          <button type="button" className="AccountOverview-moreMenuItem" onClick={() => this.handleProviderSync('Stripe')}>
                            {stripeText(this.props.language).sync}
                          </button>
                        )}
                        {data.PaymentMode === 'PayMe' && data.canSyncPayme &&
                          ['pending_for_confirmation', 'processing', 'complete'].includes(data.status) && (
                          <button type="button" className="AccountOverview-moreMenuItem" onClick={() => this.handleProviderSync('PayMe')}>
                            {paymeText(this.props.language).sync}
                          </button>
                        )}
                        {this.canManageManualPayments() && (
                          <div
                            className="AccountOverview-moreMenuItem"
                            onClick={() => {
                              this.handleToggleActionsMenu();
                              this.handleToggleEditMode();
                            }}
                          >
                            {textObj.editPayment || 'Edit Payment'}
                          </div>
                        )}
                        {this.canManageManualPayments() && this.state.receiptUrl && (
                          <div
                            className="AccountOverview-moreMenuItem"
                            onClick={() => {
                              this.handleToggleActionsMenu();
                              silentReissueReceiptBtn();
                            }}
                          >
                            {textObj.reissueReceipt || 'Re-issue'}
                          </div>
                        )}
                        {this.canManageManualPayments() && this.state.receiptUrl && (
                          <div
                            className="AccountOverview-moreMenuItem"
                            onClick={() => {
                              this.handleToggleActionsMenu();
                              resendReceiptBtn();
                            }}
                          >
                            {textObj.resendReceipt || 'Resend'}
                          </div>
                        )}
                        {isManualPayment && this.canManageManualPayments() && (
                          <Fragment>
                            {(this.state.paymentDetail.status === 'pending_for_confirmation') && (
                              <div
                                className="AccountOverview-moreMenuItem"
                                onClick={() => {
                                  this.handleToggleActionsMenu();
                                  this.handleChangeStatus('approved');
                                }}
                              >
                                {textObj.approveBtn || 'Approve Payment'}
                              </div>
                            )}
                            {(this.state.paymentDetail.status === 'pending_for_confirmation' || this.state.paymentDetail.status === 'complete') && (
                              <div
                                className="AccountOverview-moreMenuItem"
                                onClick={() => {
                                  this.handleToggleActionsMenu();
                                  this.handleDeclineClick();
                                }}
                              >
                                {textObj.declineBtn || 'Decline Payment'}
                              </div>
                            )}
                          </Fragment>
                        )}
                        {isManualPayment && this.canManageManualPayments() && (
                          <div
                            className="AccountOverview-moreMenuItem AccountOverview-moreMenuItem--danger"
                            onClick={() => {
                              this.handleToggleActionsMenu();
                              this.props.onDeleteBtn(this.state.paymentDetail.id);
                            }}
                          >
                            {textObj.deletePayment || 'Delete Payment'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Fragment>
              )}
            </div>
          </div>

          <StripeReconciliation ref={this.stripeSyncRef} payment={data} language={this.props.language} showSyncButton={false} showVerifiedLabel={false} onCheckingChange={this.handleStripeCheckingChanged}
            onRefresh={() => this.refreshStripePayment(this.props.id)} />
          <PaymeReconciliation ref={this.paymeSyncRef} onCheckingChange={this.handlePaymeCheckingChanged} payment={data} language={this.props.language} showSyncButton={false}
            onRefresh={() => this.refreshStripePayment(this.props.id)} />

          {renderObj()}
        </div>
      </div>
    );
  }
}

RenderPaymentDetail.propTypes = {
  organizationObj: PropTypes.object,
  missions: PropTypes.array,
  onEnsureAllProjects: PropTypes.func,
  onVerificationChange: PropTypes.func,
  textObj: PropTypes.object,
  receiptUrl: PropTypes.string,
  id: PropTypes.any,
  refreshKey: PropTypes.number,
  onBackBtn: PropTypes.func,
  onDeleteBtn: PropTypes.func,
  onSaveBtn: PropTypes.func,
  onChangeStatusBtn: PropTypes.func,
  language: PropTypes.string,
  upgradeHint: PropTypes.string,
};

export default RenderPaymentDetail;
